import { useMutation, useQuery } from '@tanstack/react-query';
import { api, post } from '@/lib/api/client';
import type { CustomerSearchResult } from './types';

/**
 * Customer search.
 *
 * The endpoint is a POST that takes a free-text term and returns whole user
 * records — locations and saved cards included — so there is no second request
 * to open a row. The old page ran the same search from two places (an
 * `onSearch` handler and an effect keyed on page/rows/reload) which meant every
 * pagination change re-fired the request and raced the handler; here the term
 * is the query key and TanStack Query handles the rest.
 */
export function useCustomerSearch(term: string) {
  const trimmed = term.trim();

  return useQuery({
    queryKey: ['customers', 'search', trimmed],
    queryFn: () =>
      post<CustomerSearchResult>('users/search', { SearchText: trimmed }),
    // The endpoint has no "list everything" mode; an empty box means no request.
    enabled: trimmed.length > 0,
    // Support staff re-search the same person repeatedly while on a call.
    staleTime: 60_000,
  });
}

/**
 * Marketing export — a CSV blob, not the usual JSON envelope, so it bypasses
 * `unwrap()` and goes through the axios instance directly.
 */
export function useExportMarketingCustomers() {
  return useMutation({
    mutationFn: async () => {
      const response = await api.get('marketing/customers', {
        responseType: 'blob',
      });

      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'marketing_users.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Without this the blob is held for the life of the document.
      URL.revokeObjectURL(url);
    },
  });
}
